import { 
  MultiModelConfig, 
  MultimodalResponse, 
  UploadedFile, 
  MultimodalSession,
  ResponseComparison 
} from '../types/multimodal';
import { Language } from '../hooks/useLanguage';

export class MultimodalService {
  private readonly openRouterBaseUrl = 'https://openrouter.ai/api/v1/chat/completions';

  // Processar prompt com múltiplos modelos
  async processWithMultipleModels(
    prompt: string,
    files: UploadedFile[],
    models: MultiModelConfig[],
    language: Language,
    onProgress?: (modelId: string, status: 'started' | 'completed' | 'error', response?: MultimodalResponse) => void
  ): Promise<MultimodalResponse[]> {
    const enabledModels = models.filter(m => m.enabled);
    const responses: MultimodalResponse[] = [];

    // Processar modelos em paralelo
    const promises = enabledModels.map(async (model) => {
      try {
        onProgress?.(model.id, 'started');
        
        const startTime = Date.now();
        const response = await this.processWithSingleModel(prompt, files, model, language);
        const processingTime = Date.now() - startTime;

        const multimodalResponse: MultimodalResponse = {
          id: `response-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          modelId: model.id,
          modelName: model.name,
          content: response.content,
          tokens: response.tokens,
          processingTime,
          timestamp: new Date(),
          files
        };

        onProgress?.(model.id, 'completed', multimodalResponse);
        return multimodalResponse;
      } catch (error) {
        console.error(`Error processing with model ${model.name}:`, error);
        
        const errorResponse: MultimodalResponse = {
          id: `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          modelId: model.id,
          modelName: model.name,
          content: '',
          tokens: { input: 0, output: 0, total: 0 },
          processingTime: 0,
          timestamp: new Date(),
          files,
          error: error instanceof Error ? error.message : 'Unknown error'
        };

        onProgress?.(model.id, 'error', errorResponse);
        return errorResponse;
      }
    });

    // Aguardar todos os modelos processarem
    const results = await Promise.allSettled(promises);
    
    results.forEach((result) => {
      if (result.status === 'fulfilled') {
        responses.push(result.value);
      }
    });

    return responses;
  }

  // Processar com um modelo individual
  private async processWithSingleModel(
    prompt: string,
    files: UploadedFile[],
    model: MultiModelConfig,
    language: Language
  ): Promise<{ content: string; tokens: { input: number; output: number; total: number } }> {
    const messages = this.buildMessages(prompt, files, language);
    
    const requestBody = {
      model: model.config.model,
      messages,
      max_tokens: model.config.maxTokens,
      temperature: model.config.temperature,
      stream: false
    };

    const response = await fetch(this.openRouterBaseUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${model.config.apiKey}`,
        'Content-Type': 'application/json',
        'HTTP-Referer': window.location.origin,
        'X-Title': 'Prompt Editor - Multimodal - Aldeia Viva'
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error (${model.name}): ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    
    if (!data.choices || data.choices.length === 0) {
      throw new Error(`No response from model ${model.name}`);
    }

    const content = data.choices[0].message?.content || '';
    const usage = data.usage || { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0 };

    return {
      content,
      tokens: {
        input: usage.prompt_tokens,
        output: usage.completion_tokens,
        total: usage.total_tokens
      }
    };
  }

  // Construir mensagens para a API
  private buildMessages(
    prompt: string,
    files: UploadedFile[],
    language: Language
  ): Array<{ role: 'user' | 'system'; content: any }> {
    const messages: Array<{ role: 'user' | 'system'; content: any }> = [];

    // Mensagem do sistema para contexto multimodal
    const systemMessage = language === 'pt-BR'
      ? 'Você é um assistente IA multimodal avançado. Analise cuidadosamente todos os arquivos fornecidos junto com o prompt do usuário. Forneça respostas detalhadas, precisas e relevantes considerando todo o contexto fornecido.'
      : 'You are an advanced multimodal AI assistant. Carefully analyze all provided files along with the user\'s prompt. Provide detailed, accurate, and relevant responses considering all the provided context.';

    messages.push({
      role: 'system',
      content: systemMessage
    });

    // Construir conteúdo da mensagem do usuário
    const userContent: any[] = [
      {
        type: 'text',
        text: prompt
      }
    ];

    // Adicionar arquivos ao conteúdo
    files.forEach((file, index) => {
      switch (file.type) {
        case 'image':
          if (file.base64) {
            userContent.push({
              type: 'image_url',
              image_url: {
                url: file.base64,
                detail: 'high'
              }
            });
            
            // Adicionar contexto sobre a imagem
            userContent.push({
              type: 'text',
              text: language === 'pt-BR'
                ? `\n[Imagem ${index + 1}: ${file.name}]`
                : `\n[Image ${index + 1}: ${file.name}]`
            });
          }
          break;

        case 'document':
          if (file.metadata?.extractedText) {
            userContent.push({
              type: 'text',
              text: language === 'pt-BR'
                ? `\n[Documento ${index + 1}: ${file.name}]\nConteúdo:\n${file.metadata.extractedText}`
                : `\n[Document ${index + 1}: ${file.name}]\nContent:\n${file.metadata.extractedText}`
            });
          } else if (file.base64) {
            // Para PDFs e outros documentos, incluir como base64
            userContent.push({
              type: 'text',
              text: language === 'pt-BR'
                ? `\n[Documento ${index + 1}: ${file.name}]\n[Conteúdo do documento codificado em base64: ${file.base64.substring(0, 100)}...]`
                : `\n[Document ${index + 1}: ${file.name}]\n[Base64 encoded document content: ${file.base64.substring(0, 100)}...]`
            });
          }
          break;

        case 'audio':
          userContent.push({
            type: 'text',
            text: language === 'pt-BR'
              ? `\n[Áudio ${index + 1}: ${file.name}]${file.metadata?.duration ? ` (Duração: ${Math.round(file.metadata.duration)}s)` : ''}\n[Conteúdo de áudio para transcrição]`
              : `\n[Audio ${index + 1}: ${file.name}]${file.metadata?.duration ? ` (Duration: ${Math.round(file.metadata.duration)}s)` : ''}\n[Audio content for transcription]`
          });
          break;

        case 'video':
          userContent.push({
            type: 'text',
            text: language === 'pt-BR'
              ? `\n[Vídeo ${index + 1}: ${file.name}]${file.metadata?.duration ? ` (Duração: ${Math.round(file.metadata.duration)}s)` : ''}${file.metadata?.width && file.metadata?.height ? ` (Resolução: ${file.metadata.width}x${file.metadata.height})` : ''}\n[Conteúdo de vídeo para análise]`
              : `\n[Video ${index + 1}: ${file.name}]${file.metadata?.duration ? ` (Duration: ${Math.round(file.metadata.duration)}s)` : ''}${file.metadata?.width && file.metadata?.height ? ` (Resolution: ${file.metadata.width}x${file.metadata.height})` : ''}\n[Video content for analysis]`
          });
          break;
      }
    });

    messages.push({
      role: 'user',
      content: userContent
    });

    return messages;
  }

  // Comparar respostas de múltiplos modelos
  async compareResponses(responses: MultimodalResponse[]): Promise<ResponseComparison> {
    const validResponses = responses.filter(r => !r.error && r.content.length > 0);
    
    if (validResponses.length === 0) {
      return {
        responses,
        metrics: { quality: 0, relevance: 0, completeness: 0 }
      };
    }

    // Análise básica de qualidade (pode ser expandida com IA)
    const scoredResponses = validResponses.map(response => {
      const contentLength = response.content.length;
      const hasStructure = /[.!?]/.test(response.content);
      const hasDetails = contentLength > 100;
      
      const quality = (hasStructure ? 0.4 : 0) + (hasDetails ? 0.4 : 0) + (contentLength > 200 ? 0.2 : 0);
      const relevance = Math.min(contentLength / 500, 1); // Assume que respostas mais longas são mais relevantes
      const completeness = Math.min(contentLength / 300, 1);
      
      return {
        ...response,
        score: (quality + relevance + completeness) / 3
      };
    });

    // Encontrar melhor resposta
    const winner = scoredResponses.reduce((best, current) => 
      current.score > best.score ? current : best
    );

    // Calcular métricas médias
    const avgQuality = scoredResponses.reduce((sum, r) => sum + r.score, 0) / scoredResponses.length;
    const avgRelevance = scoredResponses.reduce((sum, r) => sum + Math.min(r.content.length / 500, 1), 0) / scoredResponses.length;
    const avgCompleteness = scoredResponses.reduce((sum, r) => sum + Math.min(r.content.length / 300, 1), 0) / scoredResponses.length;

    return {
      responses,
      winner: winner.id,
      metrics: {
        quality: Math.round(avgQuality * 100) / 100,
        relevance: Math.round(avgRelevance * 100) / 100,
        completeness: Math.round(avgCompleteness * 100) / 100
      }
    };
  }

  // Criar sessão multimodal
  createSession(
    prompt: string, 
    files: UploadedFile[], 
    models: MultiModelConfig[]
  ): MultimodalSession {
    return {
      id: `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      prompt,
      files,
      models,
      responses: [],
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }

  // Atualizar sessão com respostas
  updateSessionWithResponses(
    session: MultimodalSession, 
    responses: MultimodalResponse[]
  ): MultimodalSession {
    return {
      ...session,
      responses,
      updatedAt: new Date()
    };
  }

  // Obter estatísticas da sessão
  getSessionStats(session: MultimodalSession) {
    const totalResponses = session.responses.length;
    const successfulResponses = session.responses.filter(r => !r.error).length;
    const totalTokens = session.responses.reduce((sum, r) => sum + r.tokens.total, 0);
    const avgProcessingTime = session.responses.length > 0 
      ? session.responses.reduce((sum, r) => sum + r.processingTime, 0) / session.responses.length 
      : 0;

    return {
      totalResponses,
      successfulResponses,
      errorResponses: totalResponses - successfulResponses,
      totalTokens,
      avgProcessingTime: Math.round(avgProcessingTime),
      files: session.files.length,
      models: session.models.filter(m => m.enabled).length
    };
  }

  // Exportar sessão
  exportSession(session: MultimodalSession): string {
    const data = {
      session,
      exportedAt: new Date(),
      stats: this.getSessionStats(session)
    };
    
    return JSON.stringify(data, null, 2);
  }

  // Validar se modelo suporta multimodalidade
  supportsMultimodal(modelId: string): boolean {
    const multimodalModels = [
      'anthropic/claude-3.5-sonnet',
      'anthropic/claude-3-opus',
      'anthropic/claude-3-sonnet',
      'anthropic/claude-3-haiku',
      'openai/gpt-4-vision-preview',
      'openai/gpt-4o',
      'openai/gpt-4o-mini',
      'google/gemini-pro-vision',
      'google/gemini-1.5-pro',
      'google/gemini-1.5-flash'
    ];
    
    return multimodalModels.some(model => modelId.includes(model.split('/')[1]));
  }

  // Estimar tokens para prompt multimodal
  estimateTokens(prompt: string, files: UploadedFile[]): number {
    let tokens = Math.ceil(prompt.length / 4); // Aproximação básica para texto
    
    files.forEach(file => {
      switch (file.type) {
        case 'image':
          // Imagens consomem mais tokens
          tokens += 765; // Aproximação para imagens de alta qualidade
          break;
        case 'document':
          if (file.metadata?.extractedText) {
            tokens += Math.ceil(file.metadata.extractedText.length / 4);
          } else {
            tokens += 200; // Estimativa para documentos
          }
          break;
        case 'audio':
          // Estimativa baseada na duração (aproximadamente 150 tokens por minuto)
          if (file.metadata?.duration) {
            tokens += Math.ceil((file.metadata.duration / 60) * 150);
          } else {
            tokens += 300; // Estimativa padrão
          }
          break;
        case 'video':
          // Vídeos consomem mais tokens
          if (file.metadata?.duration) {
            tokens += Math.ceil((file.metadata.duration / 60) * 500);
          } else {
            tokens += 1000; // Estimativa padrão
          }
          break;
      }
    });
    
    return tokens;
  }
}